/**
 * MOUVEMENTS DE STOCK — l'écran `mouv` de la maquette : le journal du stock, chaque entrée, chaque
 * sortie, qui, quand, sur quelle pièce.
 *
 * ⚠️ PORTAGE VERBATIM DE LA MAQUETTE (`design/`, écran `mouv`), comme `stock.tsx` et
 * `ecarts.tsx` : mêmes boîtes, mêmes teintes, tracés SVG repris tels quels. Les écarts assumés sont
 * marqués ⚠️ — chacun vient d'une décision de production, jamais d'une réinterprétation.
 *
 * ⚠️ LECTURE SEULE PAR CONSTRUCTION : « corriger, c'est écrire un nouveau mouvement — jamais
 * gommer ». Cet écran n'a AUCUN bouton d'écriture ; l'œil de chaque ligne renvoie à l'état du stock,
 * où la correction se fait par la seule porte qui sache écrire.
 */
import { exporterCsv } from './export-csv';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KpiRow, showToast, Trans, useLatest, useOverlayPanel, type KpiItem } from '@orgazone/ui-web';
import { iconPath } from '@orgazone/ui-tokens';
import {
  api, MOVEMENTS_PAGE_SIZE, type MovementKind, type MovementRow, type MovementsPage,
  type MovementsQuery, type MovementSortKey,
} from '../api';
import { Trait } from './stock-views';
import { fieldLabel } from '../vocabulary';
import { key, nombre, t, type Translatable } from '../i18n';

/** Les cinq natures — libellés, teintes et tracés de la maquette (`MVK`), verbatim. */
export const NATURES: Record<MovementKind, { l: Translatable; bg: string; fg: string; ic: string }> = {
  vente: { l: key('Vente'), bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', ic: 'M5 3h14v18l-2-2-2 2-2-2-2 2-2-2-2 2-2-2V3zM9 8h6M9 12h6M9 16h4' },
  // La boîte à couvercle est une icône de la CHARTE : son tracé se lit au registre.
  rec: { l: key('Réception'), bg: 'var(--teal-100)', fg: 'var(--teal-800)', ic: iconPath('lidBox') },
  trf: { l: key('Transfert'), bg: 'var(--blue-bg)', fg: 'var(--blue-fg)', ic: 'M7 4L3 8l4 4M3 8h13a4 4 0 0 1 0 8h-1M17 20l4-4-4-4M21 16H8a4 4 0 0 1 0-8h1' },
  inv: { l: key('Inventaire'), bg: 'var(--violet-bg)', fg: 'var(--violet-fg)', ic: 'M9 3h6v3H9zM6 5H5v16h14V5h-1M9 12l2 2 4-4' },
  adj: { l: key('Correction'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', ic: 'M4 6h12M4 12h6M4 18h10M18 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM14 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM16 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z' },
};

/** Le lexique FR du détail quand la note manque — un motif brut ne se montre jamais tel quel. */
export const RAISON: Record<string, Translatable> = {
  sale: key('Vente'), sale_return: key('Retour client'), sale_cancel: key('Annulation de vente'),
  reception: key('Réception'), initial_entry: key('Entrée initiale'), retrocession: key('Rétrocession'),
  transfer: key('Rétrocession'), reprise: key('Reprise de l’existant'), gift: key('Cadeau · geste commercial'),
  inventory: key('Inventaire'), manual_correction: key('Correction manuelle'), correction: key('Correction'),
  adjustment: key('Régularisation'), other: key('Mouvement de stock'),
  breakage: key('Casse'), theft: key('Vol / démarque'), return_supplier: key('Retour fournisseur'),
};

/**
 * LE MOTIF D'UN MOUVEMENT, tel qu'il s'affiche — résolu au RENDU.
 * ⚠️ Un motif que le référentiel ne connaît pas s’affiche TEL QUEL : c’est une donnée venue de la
 *    base, pas un de nos textes — la chercher au catalogue en ferait une clé construite à
 *    l'exécution, que rien ne peut vérifier (Forme CQ).
 */
export const motifLisible = (raison: string | null | undefined): string | undefined => {
  const connu = RAISON[raison ?? ''];
  return connu ? t(connu) : (raison ?? undefined);
};

/** Les colonnes masquables (maquette `mvCols`) — jamais la date, l'article ni la quantité. */
const COLONNES = [
  { k: 'type', l: key('Type') },
  { k: 'piece', l: key('Pièce / détail') },
  { k: 'by', l: key('Par') },
] as const;
type ColonneOptionnelle = (typeof COLONNES)[number]['k'];

const dateHeure = (ms: number): string => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * LES EN-TÊTES DE L'EXPORT — le mot du MAGASIN pour les colonnes qui sont un champ du catalogue ; les
 * mots du journal (« Quand », « Type », « Détail », « Par », « Quantité ») restent ce qu'ils sont.
 * ⚠️ UNE FONCTION APPELÉE AU CLIC, jamais une constante de module (le mot de la plateforme s'y
 *    figerait avant la lecture du contexte magasin). EXPORTÉE pour l'aller-retour export→import,
 *    éprouvé par un test : le masque d'import rattache chaque colonne de champ à son champ.
 */
export function movementsCsvHeaders(): string[] {
  return [t('Quand'), t('Type'), fieldLabel('name'), fieldLabel('articleCode'), fieldLabel('gencode'), t('Détail'), t('Par'), t('Quantité')];
}

/** Export de la vue chargée — les colonnes d'ici, la mécanique partagée (`export-csv`). */
function exporterMouvements(rows: MovementRow[]) {
  exporterCsv(
    'mouvements',
    movementsCsvHeaders(),
    rows.map((r) => [
      dateHeure(r.createdAt), t(NATURES[r.kind].l), r.articleName, r.articleCode, r.gencode,
      r.note ?? motifLisible(r.reason) ?? '', r.by, r.qty,
    ]),
  );
}

const ICO = {
  download: 'M12 4v12m0 0l-4-4m4 4l4-4M4 20h16',
  search: 'M11 4a7 7 0 100 14a7 7 0 100-14M20 20l-4-4',
  columns: 'M4 4h16v16H4zM10 4v16M16 4v16',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z',
  check: 'M5 12l5 5L20 7',
} as const;

export function Mouvements() {
  const navigate = useNavigate();
  const [page, setPage] = useState<MovementsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [nature, setNature] = useState<MovementKind | ''>('');
  const [sens, setSens] = useState<'' | 'in' | 'out'>('');
  /** `7` / `30` / `''` = tout l'historique — les trois crans de la maquette. */
  const [period, setPeriode] = useState<'7' | '30' | ''>('7');
  const [tri, setTri] = useState<MovementSortKey>('at');
  const [triSens, setTriSens] = useState<'asc' | 'desc'>('desc');
  const [hidden, setMasquees] = useState<ColonneOptionnelle[]>([]);
  const [panneauColonnes, setPanneauColonnes] = useState(false);
  // ⚠️ ÉCHAP FERME LE PANNEAU, ET LE FOCUS Y ENTRE PUIS REVIENT AU BOUTON : au seul pointeur, il ne se quittait pas au clavier.
  const colonnes = useOverlayPanel({ open: panneauColonnes, onClose: () => setPanneauColonnes(false) });

  const filtre = useMemo<MovementsQuery>(() => ({
    q: q.trim() || undefined,
    days: period === '' ? undefined : Number(period),
    kind: nature || undefined,
    direction: sens || undefined,
    sort: tri, dir: triSens,
  }), [q, period, nature, sens, tri, triSens]);

  const [suite, setSuite] = useState<MovementRow[]>([]);
  // ⚠️ UNE SEULE PORTE POUR LA LISTE, première page et lots suivants — même raison que l'état du stock : un lot parti sous
  //    l'ancien filtre ajouterait ses mouvements à la liste du nouveau.
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    setLoading(true);
    setErreur(null);
    // Un lot en cours devient dépassé : il ne rendra plus la main, c'est la nouvelle première page qui la reprend.
    setChargementSuite(false);
    api.stockMovements(filtre)
      .then((p) => { if (!isLatest()) return; setPage(p); setSuite([]); setChaineMorte(false); })
      .catch((e: unknown) => { if (isLatest()) setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (isLatest()) setLoading(false); });
  }, [filtre, latest]);

  // La recherche attend 300 ms — sans quoi chaque frappe interrogerait le journal entier.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const lines = useMemo(() => [...(page?.rows ?? []), ...suite], [page, suite]);
  const [chargementSuite, setChargementSuite] = useState(false);
  /** ⚠️ Même verrou que l'état du stock : un lot vide (données bougées entre deux lots) TUE la chaîne. */
  const [chaineMorte, setChaineMorte] = useState(false);
  const chargerSuite = () => {
    const isLatest = latest();
    setChargementSuite(true);
    api.stockMovements({ ...filtre, offset: lines.length })
      .then((p) => {
        if (!isLatest()) return;
        if (p.rows.length === 0) setChaineMorte(true);
        setSuite((prev) => [...prev, ...p.rows]);
      })
      .catch((e: unknown) => {
        if (!isLatest()) return;
        setChaineMorte(true);
        showToast(t('Suite illisible : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err');
      })
      .finally(() => { if (isLatest()) setChargementSuite(false); });
  };

  /**
   * ⚠️ UNE VUE FILTRÉE SE CHARGE EN ENTIER — même décision que l'état du stock (2026-08-27).
   * La PÉRIODE n'est pas un filtre pour cette règle : c'est la fenêtre de travail par défaut.
   */
  const filtreActif = filtre.q != null || filtre.kind != null || filtre.direction != null;
  useEffect(() => {
    if (!filtreActif || loading || chargementSuite || chaineMorte) return;
    if (page == null || page.total <= lines.length) return;
    chargerSuite();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la chaîne se re-déclenche sur l'arrivée de chaque lot
  }, [filtreActif, loading, chargementSuite, chaineMorte, page, lines.length]);

  // Les colonnes masquées suivent la PERSONNE (même portée que l'état du stock : `bo_table_prefs`).
  useEffect(() => {
    api.tablePrefs('mouvements')
      .then((p) => setMasquees((p.layout?.hidden ?? []).filter((k): k is ColonneOptionnelle => COLONNES.some((c) => c.k === k))))
      .catch(() => undefined); // best-effort assumé : sans préférence, tout est visible
  }, []);
  const basculerColonne = (k: ColonneOptionnelle) => {
    const prochaines = hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k];
    setMasquees(prochaines);
    void api.saveTablePrefs('mouvements', { layout: { order: COLONNES.map((c) => c.k), hidden: prochaines } })
      .catch((e: unknown) => showToast(t('Réglage non mémorisé : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'));
  };
  const visible = (k: ColonneOptionnelle) => !hidden.includes(k);

  const trierPar = (k: MovementSortKey) => {
    if (tri === k) { setTriSens((x) => (x === 'asc' ? 'desc' : 'asc')); return; }
    setTri(k);
    setTriSens(k === 'at' || k === 'q' ? 'desc' : 'asc'); // maquette : date et quantité d'abord décroissantes
  };

  const resume = page?.summary;
  /** Les 4 repères de la maquette (`mvKpis`) — sur la vue FILTRÉE entière, tracés verbatim. */
  const kpis: KpiItem[] = resume == null ? [] : [
    { label: t('Pièces entrées'), value: `+${resume.entered.toLocaleString('fr-FR')}`, sub: t('réceptions, corrections positives, écarts d’inventaire'),
      icon: 'M12 4v12m0 0l-4-4m4 4l4-4M4 20h16', tone: 'teal' },
    { label: t('Pièces sorties'), value: `−${resume.exited.toLocaleString('fr-FR')}`, sub: t('ventes, transferts, corrections négatives'),
      icon: 'M12 21V9m0 0l-4 4m4-4l4 4M4 4v-.5h16V4', tone: 'rose' },
    { label: t('Solde net'), value: t('{signe}{n} pièce', { signe: resume.entered - resume.exited >= 0 ? '+' : '−', n: Math.abs(resume.entered - resume.exited) }), sub: t('entrées moins sorties, vue filtrée'),
      icon: 'M18.5 5.5A7.5 7.5 0 0 0 13 3.5a8 8 0 0 0 0 17 7.5 7.5 0 0 0 5.5-2M3.5 10h10M3.5 14h8.5', tone: 'blue' },
    { label: t('Mouvements'), value: resume.count.toLocaleString('fr-FR'), sub: t('lignes du journal, vue filtrée'),
      icon: iconPath('lines'), tone: 'ink' },
  ];

  /** Les puces de nature (maquette `mvChips`) : « Tout » + les cinq, comptées SANS le filtre de nature. */
  const puces = useMemo(() => {
    const kinds = resume?.kinds ?? { vente: 0, rec: 0, trf: 0, inv: 0, adj: 0 };
    const total = Object.values(kinds).reduce((sum, x) => sum + x, 0);
    return [
      { v: '' as const, l: t('Tout'), n: total },
      ...(Object.keys(NATURES) as MovementKind[]).map((k) => ({ v: k, l: t(NATURES[k].l), n: kinds[k] })),
    ];
  }, [resume]);

  const gridT = ['118px', visible('type') && '128px', 'minmax(0,1.45fr)', visible('piece') && 'minmax(0,1.15fr)', visible('by') && '92px', '84px', '42px']
    .filter(Boolean).join(' ');
  const enTetes = [
    { l: t('Quand'), k: 'at' as MovementSortKey, jc: 'flex-start', col: null as ColonneOptionnelle | null },
    { l: t('Type'), k: 'type' as MovementSortKey, jc: 'flex-start', col: 'type' as ColonneOptionnelle | null },
    { l: t('Article'), k: 'art' as MovementSortKey, jc: 'flex-start', col: null as ColonneOptionnelle | null },
    { l: t('Pièce / détail'), k: 'piece' as MovementSortKey, jc: 'flex-start', col: 'piece' as ColonneOptionnelle | null },
    { l: t('Par'), k: 'by' as MovementSortKey, jc: 'flex-start', col: 'by' as ColonneOptionnelle | null },
    { l: t('Qté'), k: 'q' as MovementSortKey, jc: 'flex-end', col: null as ColonneOptionnelle | null },
  ].filter((h) => !h.col || visible(h.col));

  const troncature = page != null && page.total > lines.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* ── L'en-tête commune : titre + sous-titre à gauche, export à droite ─────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Mouvements de stock')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='Le journal du stock : chaque entrée, chaque sortie, <1>qui, quand, sur quelle pièce</1>. Corriger, c’est écrire un nouveau mouvement — jamais gommer.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        <button
          type="button"
          onClick={() => { if (lines.length > 0) { exporterMouvements(lines); showToast(t('{n} ligne exportée', { n: lines.length }), 'ok'); } }}
          disabled={lines.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 15px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: lines.length ? 'pointer' : 'default', opacity: lines.length ? 1 : 0.5 }}
        >
          <Trait d={ICO.download} size={15} />
          
          {t('Export Excel')}
        </button>
      </div>

      {/* ── Les 4 repères — sur la vue filtrée entière ───────────────────────────────────────────── */}
      {resume && (
        <KpiRow items={kpis} style={{ marginBottom: 10 }} />
      )}

      {/* ── LA CARTE : barre d'outils + journal ──────────────────────────────────────────────────── */}
      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px', borderBottom: '1px solid var(--ink-200)' }}>
          {/* ⚠️ Capsule `field-search` du kit — le même champ partout (règle utilisateur 2026-08-27),
              là où la maquette dessinait sa propre loupe. */}
          <div className="field-search" style={{ flex: '1 1 210px', maxWidth: 330 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
            </svg>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              /* ⚠️ Le placeholder promet EXACTEMENT ce que le SQL tient : article, code, gencode,
                 note de mouvement, opérateur — vérifié par test. */
              placeholder={t('Article, code, pièce, opérateur…')}
              aria-label={t('Rechercher dans le journal')}
            />
          </div>
          {puces.map((c) => {
            const on = nature === c.v;
            return (
              <button
                key={c.v || 'tout'} type="button" onClick={() => setNature(c.v)} aria-pressed={on}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 11px', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                {c.l}
                <span style={{ fontSize: '10.5px', fontWeight: 800, opacity: 0.65, fontVariantNumeric: 'tabular-nums' }}>{c.n.toLocaleString('fr-FR')}</span>
              </button>
            );
          })}
          <span style={{ flex: 1 }} />
          {/* ⚠️ Segmenté fait main et non le `Segmented` du kit : l'actif porte une COULEUR SÉMANTIQUE
              (+ entrées teal, − sorties rose) que le kit ne sait pas rendre — ce n'est pas un galet. */}
          <span style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', overflow: 'hidden', flexShrink: 0 }}>
            {([{ v: '' as const, l: t('Tout') }, { v: 'in' as const, l: t('+ Entrées') }, { v: 'out' as const, l: t('− Sorties') }]).map((s2) => {
              const on = sens === s2.v;
              const bg = on ? (s2.v === 'in' ? 'var(--teal-100)' : s2.v === 'out' ? 'var(--rose-bg)' : 'var(--ink-100)') : 'transparent';
              const fg = on ? (s2.v === 'in' ? 'var(--teal-800)' : s2.v === 'out' ? 'var(--rose-fg)' : 'var(--ink-800)') : 'var(--ink-500)';
              return (
                <button key={s2.v || 'tout'} type="button" onClick={() => setSens(s2.v)} aria-pressed={on}
                  style={{ height: 36, padding: '0 12px', border: 'none', background: bg, color: fg, fontSize: 'var(--t-dense)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  {s2.l}
                </button>
              );
            })}
          </span>
          <select
            aria-label={t('Période')} value={period} onChange={(e) => setPeriode(e.target.value as '7' | '30' | '')}
            style={{ height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: '12.5px', fontWeight: 600, outline: 'none', flexShrink: 0 }}
          >
            <option value="7">{t('7 derniers jours')}</option>
            <option value="30">{t('30 derniers jours')}</option>
            <option value="">{t('Tout l’historique')}</option>
          </select>
          <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <button
              type="button" onClick={() => setPanneauColonnes((o) => !o)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 12px', border: `1px solid ${hidden.length ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: hidden.length ? 'var(--teal-100)' : 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}
            >
              <Trait d={ICO.columns} size={14} stroke={1.8} />
              
              {hidden.length ? t('Colonnes · {n} masquée', { n: hidden.length }) : t('Colonnes')}
            </button>
            {panneauColonnes && (
              <>
                <div onClick={() => setPanneauColonnes(false)} style={{ position: 'fixed', inset: 0, zIndex: 590 }} />
                <div ref={colonnes.panelRef} role="dialog" aria-label={t('Colonnes affichées')} style={{ position: 'absolute', right: 0, top: 42, zIndex: 600, width: 274, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: '0 18px 44px -12px rgba(12,14,16,.28)', overflow: 'hidden', animation: 'pop .15s cubic-bezier(.2,.7,.3,1)' }}>
                  <div style={{ padding: '11px 14px 5px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Colonnes affichées')}</div>
                  <div style={{ padding: '2px 6px 6px' }}>
                    {[{ l: t('Quand') }, { l: t('Article') }, { l: t('Qté') }].map((c) => (
                      <div key={c.l} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: 'var(--r-xs)', cursor: 'default' }}>
                        <span style={{ width: 17, height: 17, borderRadius: 'var(--r-xs)', border: '1.5px solid var(--ink-300)', background: 'var(--ink-300)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Trait d={ICO.check} size={11} stroke={3} /></span>
                        <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-400)' }}>{t(c.l)}</span>
                      </div>
                    ))}
                    {COLONNES.map((c) => {
                      const on = visible(c.k);
                      return (
                        <div key={c.k} role="button" tabIndex={0} onClick={() => basculerColonne(c.k)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculerColonne(c.k); } }} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: 'var(--r-xs)', cursor: 'pointer' }}>
                          <span style={{ width: 17, height: 17, borderRadius: 'var(--r-xs)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: on ? 'var(--teal-500)' : 'var(--paper)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on ? <Trait d={ICO.check} size={11} stroke={3} /> : null}</span>
                          <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-800)' }}>{t(c.l)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ padding: '8px 14px 10px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                    
                    {t('Visibilité — mémorisée sur votre profil (tous vos postes), par tableau')}
                  </div>
                </div>
              </>
            )}
          </span>
        </div>

        {/* ── Le journal — borné et défilant DANS la carte (leçon des ascenseurs, 2026-08-27) ─────── */}
        {erreur ? (
          <div style={{ padding: '30px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--rose-fg)' }}>{t('Journal illisible')}</div>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3 }}>{t('Le magasin n’a pas répondu : {erreur}', { erreur })}</div>
            <button type="button" onClick={load} style={{ marginTop: 12, height: 34, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>{t('Réessayer')}</button>
          </div>
        ) : loading && !page ? (
          <div style={{ padding: 30, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture du journal…')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {/* ⚠️ `position: relative` sur le conteneur borné — la leçon de l'ascenseur fantôme :
                un défilement STATIQUE laisse les absolus de ses lignes se rattacher au viewport. */}
            <div style={{ minWidth: 960, maxHeight: 'min(64vh, 760px)', overflowY: 'auto', position: 'relative' }}>
              <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'grid', gridTemplateColumns: gridT, gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                {enTetes.map((h) => (
                  <button
                    key={h.l} type="button" title={t('Trier')} onClick={() => trierPar(h.k)}
                    style={{ border: 'none', background: 'transparent', padding: 0, margin: 0, font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', color: tri === h.k ? 'var(--teal-700)' : 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: h.jc, whiteSpace: 'nowrap', minWidth: 0 }}
                  >
                    {h.l}
                    <span style={{ fontSize: 'var(--t-eyebrow)' }}>{tri === h.k ? (triSens === 'asc' ? '▲' : '▼') : ''}</span>
                  </button>
                ))}
                <div />
              </div>
              {lines.map((r) => {
                const nat = NATURES[r.kind];
                const detail = r.note ?? motifLisible(r.reason) ?? '—';
                return (
                  <div key={r.id} style={{ display: 'grid', gridTemplateColumns: gridT, gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center', fontSize: '12.5px' }}>
                    <div style={{ color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{dateHeure(r.createdAt)}</div>
                    {visible('type') && (
                      <div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 8px', borderRadius: 'var(--r-xs)', background: nat.bg, color: nat.fg, fontSize: '10.5px', fontWeight: 800, whiteSpace: 'nowrap' }}>
                          <Trait d={nat.ic} size={11} stroke={2} />
                          {t(nat.l)}
                        </span>
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.articleName}</div>
                      <div style={{ fontSize: '10.5px', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>{r.articleCode || r.gencode}</div>
                    </div>
                    {visible('piece') && (
                      <div style={{ minWidth: 0, color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={detail}>{detail}</div>
                    )}
                    {visible('by') && (
                      <div style={{ color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.by}</div>
                    )}
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-xs)', background: r.qty > 0 ? 'var(--teal-100)' : 'var(--rose-bg)', color: r.qty > 0 ? 'var(--teal-800)' : 'var(--rose-fg)', fontSize: 'var(--t-dense)', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                        {r.qty > 0 ? `+${r.qty}` : `−${Math.abs(r.qty)}`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button" aria-label={t('Voir le stock de « {v} »', { v: r.articleName || r.gencode })} title={t('Voir le stock et l’historique de cet article')}
                        onClick={() => navigate(`/stock?fiche=${encodeURIComponent(r.gencode)}`)}
                        style={{ width: 28, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"><path d={ICO.eye} /><circle cx="12" cy="12" r="2.6" /></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
              {lines.length === 0 && (
                <div style={{ padding: 34, textAlign: 'center', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
                  
                  {t('Aucun mouvement ne correspond — élargissez la période ou retirez un filtre.')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Le pied : le compte, la suite ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
          {page == null ? '' : troncature
            ? t('{n} lignes affichées sur {total} — l’export reprend les lignes affichées.', { n: lines.length, total: nombre(page.total) })
            : lines.length === 0 ? ''
              : t('{n} mouvement · l’export reprend exactement cette vue.', { n: lines.length })}
        </span>
        {troncature && filtreActif && !chaineMorte && (
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
            {t('Chargement de la suite… {v} / {v2}', { v: lines.length.toLocaleString('fr-FR'), v2: page!.total.toLocaleString('fr-FR') })}
          </span>
        )}
        {troncature && chaineMorte && (
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', whiteSpace: 'nowrap' }}>
            {t('Chargement interrompu à {v} — modifiez le filtre pour relancer', { v: lines.length.toLocaleString('fr-FR') })}</span>
        )}
        {troncature && !filtreActif && (
          /* ⚠️ 1 000 par lot et non les « 60 lignes de plus » de la maquette : la cadence a été
              alignée sur l'état du stock (décision utilisateur 2026-08-27), et ces lignes-ci ne
              coûtent même pas de rejeu de PMP. */
          <button
            type="button" onClick={chargerSuite} disabled={chargementSuite}
            style={{ height: 32, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: chargementSuite ? 'default' : 'pointer' }}
          >
            {chargementSuite ? t('Chargement…') : t('Afficher les {n} suivantes', { n: Math.min(MOVEMENTS_PAGE_SIZE, page!.total - lines.length) })}
          </button>
        )}
      </div>
    </div>
  );
}
